import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { color, Icon, Input } from '@wf/components';
import { Grid } from '@wf/sw-ui-kit';

import Scrollable from '../../../../components/ListWithContentLayout/components/Scrollable';
import { ListItemState } from '../../../../components/ListWithContentLayout/types';
import ListWithContentLayout from '../../../../components/ListWithContentLayout';
import { type IItemResponse } from '../../../License/types/response';
import { ArrayFields } from '../../../License/types/fieldEnums';
import Separator from '../../../../components/Separator';
import ItemComparisonCard from './ItemComparisonCard';
import { Tab } from '../../../License/types/enums';
import ComparisonView from '../ComparisonView';

import useComparisonParams from '../../hooks/useComparisonParams';
import useItemFields from '../../hooks/useItemFields';
import useListItems from '../../hooks/useListItems';

import { versionComparisonSelector } from '../../../../store/audit/selectors';

import styles from './ItemsComparison.module.scss';

function ItemsComparison() {
  const { t } = useTranslation();
  const { lpcoResponse } = useSelector(versionComparisonSelector);
  const { leftEpochMillis, rightEpochMillis } = useComparisonParams();

  const itemFields = useItemFields();
  const [items, itemStates] = useListItems(lpcoResponse.items ?? [], ArrayFields.items);

  const [selectedItem, setSelectedItem] = useState<{ values: IItemResponse; index: number } | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setSelectedItem(null);
  }, [leftEpochMillis, rightEpochMillis]);

  const searchText = search.trim().toLowerCase();

  return (
    <ListWithContentLayout
      sidebar={
        <>
          <Grid gap={20} className="p-b-8">
            <span className="fs-20 fw-semibold typography">{t('All Items')}</span>
            {items.length > 0 && (
              <Input
                leftIcon={<Icon name="ic_search" color={color('typography', 'light')} size={15} />}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('Search')}
              />
            )}
          </Grid>
          <Scrollable>
            <Grid gap={8}>
              {items.map((item, index) => (
                <ItemComparisonCard
                  onClick={() => setSelectedItem({ values: item, index })}
                  isActive={selectedItem?.index === index}
                  itemState={itemStates[item.id]}
                  searchText={searchText}
                  index={index}
                  key={item.id}
                  {...item}
                />
              ))}
            </Grid>
          </Scrollable>
        </>
      }
    >
      <div className={styles.container} key={selectedItem?.values.id}>
        {selectedItem && (
          <>
            <div className="fs-20 fw-semibold  typography flex gap-8 items-center m-b-16">
              <span>{t('Item Number')}</span> <span>{selectedItem.index + 1}</span>
            </div>
            <Separator className={styles.separator} />
            <Scrollable>
              <ComparisonView
                showBothSides={
                  itemStates[selectedItem.values.id] !== ListItemState.Added &&
                  itemStates[selectedItem.values.id] !== ListItemState.Deleted
                }
                pathPrefix={`/${ArrayFields.items}/${selectedItem.values.id}/`}
                values={selectedItem.values}
                fields={itemFields}
                tab={Tab.ITEM}
              />
            </Scrollable>
          </>
        )}
      </div>
    </ListWithContentLayout>
  );
}

export default ItemsComparison;
